import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import styled from "styled-components/macro";

import { useHasHideMakeAPaymentButton } from "Flocknote/stateHooks";

import { useUserPledgesList } from "CrossFeatureComponents/UserPledgesList/UserPledgesListContext";
import { useRecurringGifts } from "CrossFeatureComponents/UserRecurringGifts/RecurringGiftsContext";
import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrIcon, { FnrIconWrapper } from "UI/FnrIcon";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import ErrorBoundaryFallback from "./ErrorBoundaryFallback";
import "./RecurringAndPledgesContainerQueries.scss";
import RecurringAndPledgesTabBar from "./RecurringAndPledgesTabBar";
import PledgesScreen from "./Screens/PledgesScreen";
import RecurringGiftsScreen from "./Screens/RecurringGiftsScreen";
import { useShouldShowPledgesTab } from "./useShouldShowPledgesTab";

export default function RecurringAndPledges() {
  const { state: pledgesState } = useUserPledgesList();
  const [givingTab, setGivingTab] = useState(
    localStorage.getItem("manageMyGivingPageTab") || "recurring"
  );
  const [hasHideMakeAPaymentButton] = useHasHideMakeAPaymentButton();
  const [givingTabToShow, setGivingTabToShow] = useState(givingTab);
  const [animateHeight, setAnimateHeight] = useState(false);
  const [styleHeight, setStyleHeight] = useState("auto");

  const isMounted = useComponentIsMounted();

  const showPledgingTab = useShouldShowPledgesTab();

  const updateGivingTab = (newTab) => {
    setGivingTab(newTab);
    localStorage.setItem("manageMyGivingPageTab", newTab);
  };
  const {
    state: { userID },
  } = useManageMyGivingPage();
  const { state: recurringState } = useRecurringGifts();

  useEffect(
    function handleIfPledgingTabHiddenAndSelected() {
      const userPledgesAreLoaded =
        pledgesState.fetchPledgesStatus === "success";

      if (userPledgesAreLoaded && !showPledgingTab && givingTab === "pledges") {
        updateGivingTab("recurring");
      }
    },
    [givingTab, showPledgingTab, pledgesState.fetchPledgesStatus]
  );

  const recurringRef = useRef();
  const pledgesRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    if (!isMounted) return;
    setStyleHeight(containerRef.current.getBoundingClientRect().height);
    setGivingTabToShow(givingTab);
    //setStyleHeight(refToUse.current.getBoundingClientRect().height);

    // eslint-disable-next-line
  }, [givingTab]);

  useEffect(() => {
    if (!isMounted) return;
    const refToUse = givingTab === "recurring" ? recurringRef : pledgesRef;
    setAnimateHeight(refToUse.current.getBoundingClientRect().height);

    // eslint-disable-next-line
  }, [givingTabToShow]);

  const showChild = {
    x: 0,
    opacity: 1,
    transition: { delay: 0.05 },
  };

  const hideChild = {
    opacity: 0,
    transition: { type: "tween", duration: 0.2, ease: "easeOut" },
  };

  const containerHeightDoneResizing = () => {
    setStyleHeight("auto");
    setTimeout(() => (containerRef.current.style.height = "auto"), 100);
  };

  const readyToShow = recurringState.recurringGiftsListStatus === "success";

  if (!readyToShow) return null;

  return (
    <ErrorBoundary fallbackRender={() => null}>
      <Wrapper className="recurring_and_pledges_container">
        <RecurringAndPledgesTabBar
          givingTab={givingTab}
          updateGivingTab={updateGivingTab}
          showPledgingTab={showPledgingTab}
          showRecurringTab={!(hasHideMakeAPaymentButton ?? true)}
        />
        <Container
          style={{ height: styleHeight }}
          animate={{ height: animateHeight }}
          onAnimationComplete={containerHeightDoneResizing}
          ref={containerRef}
        >
          {!(hasHideMakeAPaymentButton ?? true) ? (
            <Screen
              className={classNames({
                hidden: givingTabToShow !== "recurring",
              })}
              ref={recurringRef}
              animate={
                givingTabToShow === "recurring"
                  ? showChild
                  : { ...hideChild, x: -40 }
              }
            >
              <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
                <RecurringGiftsScreen />
              </ErrorBoundary>
            </Screen>
          ) : null}

          {showPledgingTab ? (
            <Screen
              className={classNames({ hidden: givingTabToShow !== "pledges" })}
              animate={
                givingTabToShow === "pledges"
                  ? showChild
                  : { ...hideChild, x: 40 }
              }
              ref={pledgesRef}
            >
              <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
                <PledgesScreen />
              </ErrorBoundary>
            </Screen>
          ) : null}
        </Container>
        <PaymentMethodsButtonWrapper className="payment_methods_button_below">
          <A
            href={`/dashboard/user/${userID}#wallet`}
            target="_blank"
            rel="noreferrer"
          >
            <PaymentMethodsText whileTap={{ scale: 0.94 }}>
              <FnrIcon fad fa-wallet />
              My Payment Methods
            </PaymentMethodsText>
          </A>
        </PaymentMethodsButtonWrapper>
      </Wrapper>
    </ErrorBoundary>
  );
}
const Wrapper = styled.div`
  grid-area: recurring;
  align-self: start;
  border-radius: 24px;
  background: linear-gradient(
    140deg,
    var(--bgGradient1) 0%,
    var(--bgGradient2) 100%
  );
  flex-shrink: 0;
  flex-grow: 1;
`;

const Container = styled(motion.div)`
  position: relative;
  overflow: hidden;
`;
const Screen = styled(motion.div)`
  transition: visibility 0.5s;

  &.hidden {
    visibility: hidden;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
  }
`;

const PaymentMethodsButtonWrapper = styled.div`
  padding-left: 20px;
  padding-bottom: 16px;
  display: none;
`;
const PaymentMethodsText = styled(motion.div)`
  font-size: 18px;
  gap: 6px;
  display: flex;
  align-items: center;
  position: relative;
  padding: 0 1px;
  & ${FnrIconWrapper} {
    font-size: 20px;
  }
`;
const A = styled.a`
  text-decoration: none !important;
`;
